import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const CreateOrgFormSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
});

interface OrganizationSelectorProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const { user } = useAuthStore();
  const {
    organizations,
    addOrganization,
    selectedOrganizationId,
    setSelectedOrganizationId,
  } = useOrganizationsStore();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);

  // Support both controlled (from AppLayout) and uncontrolled (from old Sidebar) usage
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;

  // Uncontrolled mode: auto-open when no org selected
  useEffect(() => {
    if (controlledOpen === undefined && !selectedOrganizationId && organizations.length > 0) {
      setInternalOpen(true);
    }
  }, [controlledOpen, selectedOrganizationId, organizations]);

  const handleOrganizationChange = (orgId: string) => {
    setSelectedOrganizationId(orgId);
    setOpen(false);
  };

  const handleClose = () => {
    if (!selectedOrganizationId && organizations.length > 0) {
      return;
    }
    setOpen(false);
  };

  const form = useForm<z.infer<typeof CreateOrgFormSchema>>({
    resolver: zodResolver(CreateOrgFormSchema),
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof CreateOrgFormSchema>) => {
    try {
      const newOrg = await api.createOrganization({ name: data.name });
      addOrganization(newOrg);
      toast({ title: "Organization created successfully" });
      form.reset();
      setIsDialogOpen(false);
      setSelectedOrganizationId(newOrg.id);
      setOpen(false);
    } catch (error) {
      console.error("Failed to create organization:", error);
      toast({
        title: "Failed to create organization",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-hidden bg-background">
        <div className="container mx-auto flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col px-4 py-6 sm:px-6 sm:py-10">
          <div className="flex shrink-0 flex-col items-stretch gap-4 pb-4 border-b sm:pb-6">
            <h2 className="text-xl font-bold sm:text-2xl">Select an Organization</h2>
            <Button onClick={() => setIsDialogOpen(true)}>
              Create Organization
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto py-4 sm:py-6">
            {organizations.length === 0 ? (
              <Alert>
                <AlertTitle>No Organizations Found</AlertTitle>
                <AlertDescription>
                  You currently have no organizations. Please create one to
                  get started.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="grid w-full max-w-2xl gap-3 sm:gap-4">
                {organizations.map((org) => (
                  <Card
                    key={org.id}
                    className={`cursor-pointer hover:bg-muted transition-colors p-4 ${
                      selectedOrganizationId === org.id
                        ? "border-2 border-primary"
                        : ""
                    }`}
                    onClick={() => handleOrganizationChange(org.id)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="min-w-0 break-words text-base font-semibold sm:text-lg">{org.name}</h3>
                      {selectedOrganizationId === org.id && (
                        <Check className="h-5 w-5 shrink-0 text-primary" />
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <div className="flex shrink-0 justify-end pt-4 border-t sm:pt-6">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={handleClose}
              disabled={!selectedOrganizationId && organizations.length > 0}
            >
              {selectedOrganizationId ? "Close" : "Cancel"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>
              Fill in the form below to create a new organization.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Organization Name"
                    {...form.register("name")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit">Create Organization</Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrganizationSelector;
